import React from "react";
import { StyleSheet, View, TouchableOpacity, Platform, SafeAreaView } from "react-native";
import { Grid, Container, Row, Column } from "ui-grid";
import * as Linking from "expo-linking";
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { AppMode, PaymentActionTypes } from "../../../enums";
import { isBalanceError } from "../../../utils/money";
import { translate } from "../../../i18n/translate";
import {
  Icon,
  MenuOption,
  MenuOptionObject,
  PopupMenu,
  ProgressIndicator,
  theme,
  Text,
  Logo,
} from "ui-components";
import { useDispatch, useSelector } from "react-redux";
import {
  setBlacklist,
  setBlacklistRecovery,
  signOutUser,
  updateAppMode,
} from "../../../redux/actions/actionCreators";
import { RootState } from "../../../redux/reducers";
import { domusoResponsiveStyleHelper } from "../../../screens/new/utils/domuso-responsive";
import automationTestingLocators from "../../../screens/new/utils/automation-testing-locators.json";
import { logoAction, menuOptions } from "../../../screens/new/utils/business-logic-header";

const { PAYMENT_STARTED, PAYMENT_IN_REVIEW, PAYMENT_CREATED } = PaymentActionTypes;

export function Header({ options, navigation, route }: any) {
  const dispatch = useDispatch();
  const {
    useLight,
    omitBackButton,
    showBalance,
    showProperty,
    propName,
    propertyAddress,
    paymentStatus,
    balanceDue,
    progressStep,
    omitTitleHeader,
    omitProfileIcon,
  } = options;
  const paymentIsActive = [PAYMENT_STARTED, PAYMENT_IN_REVIEW, PAYMENT_CREATED].includes(
    paymentStatus
  );
  const hasBalanceError = isBalanceError(balanceDue);
  const styles = getStyles({ useLight, showProperty, hasBalanceError });

  const title =
    options.headerTitle !== undefined
      ? options.headerTitle
      : options.title !== undefined
      ? options.title
      : route.name;

  const currentReservationId = useSelector(
    (state: RootState) => state.reservation.currentReservationId
  );
  const allReservations = useSelector((state: RootState) => state.reservation.reservations);
  const userInfo = useSelector((state) => state.auth.userInfo);
  const isLoggedIn = Boolean(userInfo.username);

  const popupOptions: MenuOptionObject[] = menuOptions({
    currentReservationId,
    allReservations,
    options,
    isLoggedIn,
  })
    .map((item) => {
      const payload = {
        order: item.order,
        iconName: item.iconName,
        iconColor: item.iconColor,
        title: translate(item.title) as string,
        titleColor: item.titleColor,
        ...automationTestingLocators.header[item.testingHooks],
      };

      if (item.type === "internal") {
        payload.onPress = () => {
          return navigation.navigate(item.path, { screen: item.pathSecondary });
        };
      }

      if (item.type === "external") {
        payload.onPress = () => {
          if (Platform.OS == "web") {
            window.open(item.path, "_blank");
          } else {
            Linking.openURL(item.path);
          }
        };
      }

      if (item.type === "logout") {
        payload.onPress = () => {
          dispatch(setBlacklist(false));
          dispatch(setBlacklistRecovery(false));
          dispatch(updateAppMode(AppMode.NORMAL));
          dispatch(signOutUser());
        };
      }

      return payload;
    })
    .sort((a, b) => a.order - b.order);

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.navTop}>
        <View style={styles.navTopCol}>
          <TouchableOpacity
            style={styles.domusoIconButtonStyle}
            onPress={() => {
              return navigation.navigate(logoAction({ options, navigation, route })["path"]);
            }}
            activeOpacity={0.9}
            {...automationTestingLocators.header.domusoIconButton}>
            <Logo />
          </TouchableOpacity>
        </View>
        <View
          style={[
            styles.menuProfileIconContainer,
            { display: !omitProfileIcon ? "flex" : "none" },
          ]}>
          <PopupMenu
            menuTriggerProps={{
              customStyles: {
                TriggerTouchableComponent: TouchableOpacity,
                triggerTouchable: {
                  activeOpacity: 0.8,
                  style: styles.triggerTouchableStyle,
                },
              },
            }}
            {...automationTestingLocators.header.profileIconButton}
            renderOptions={() => <MenuOption options={popupOptions} />}>
            <Icon name={"profile"} size={theme.iconSize.medium} color={theme.colors.primary} />
          </PopupMenu>
        </View>
      </View>
      <View style={[styles.nav, { display: !omitTitleHeader ? "flex" : "none" }]}>
        <Grid>
          <Container>
            <Row>
              <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
              <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }}>
                <View style={styles.navInner}>
                  <View style={{ width: "20%" }}>
                    {!omitBackButton && (
                      <TouchableOpacity
                        onPress={() => {
                          if (navigation.canGoBack()) {
                            navigation.goBack();
                          }
                        }}
                        style={styles.back}
                        {...automationTestingLocators.header.headerBackButton}>
                        <MaterialCommunityIcons
                          name="chevron-left"
                          size={30}
                          color={theme.colors.secondary}
                        />
                      </TouchableOpacity>
                    )}
                  </View>
                  <View style={{ width: "60%" }}>
                    <Text style={styles.title} {...automationTestingLocators.header.headerTitle}>
                      {title}
                    </Text>
                  </View>
                  <View style={{ width: "20%" }}></View>
                </View>
              </Column>
            </Row>
          </Container>
        </Grid>
      </View>
      {showBalance && (
        <View style={styles.content}>
          {showProperty && (
            <View style={styles.property}>
              <View style={styles.cardContainer}>
                <View style={styles.textContainer}>
                  <Text
                    style={styles.headline}
                    {...automationTestingLocators.header.headerPropertyCardPropertyName}>
                    {propName}
                  </Text>
                  <Text
                    style={{ textAlign: "center" }}
                    {...automationTestingLocators.header.headerPropertyCardPropertyAddress}>
                    {propertyAddress}
                  </Text>
                </View>
              </View>
            </View>
          )}

          {paymentIsActive && (
            <View style={styles.paymentSteps}>
              <View style={{ marginTop: 15 }}></View>
              <ProgressIndicator
                stepCount={2}
                currentPosition={progressStep}
                labels={["Payment Details", "Payment Summary"]}
              />
            </View>
          )}
        </View>
      )}
    </SafeAreaView>
  );
}

const getStyles = ({ useLight, showProperty, hasBalanceError }: any) => {
  return StyleSheet.create({
    container: {
      backgroundColor: theme.colors.white,
      paddingBottom: showProperty ? 12 : 0,
      marginBottom: showProperty ? 24 : 0,
      borderBottomColor: theme.colors.smokeWhite,
      borderBottomWidth: useLight ? 1 : 0,
    },
    navTop: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 5,
      backgroundColor: theme.colors.white,
      shadowOffset: { width: 0, height: 10 },
      shadowColor: "#85858526",
      shadowOpacity: 1,
      shadowRadius: 10,
      elevation: 10,
      ...domusoResponsiveStyleHelper({
        webPhone: { paddingLeft: 10 },
        webTablet: { paddingLeft: 20 },
        webDesktop: { paddingLeft: 30 },
        iosPhone: { paddingLeft: 10 },
        iosTablet: { paddingLeft: 20 },
        androidPhone: { paddingLeft: 10, shadowColor: theme.colors.secondary },
        androidTablet: { paddingLeft: 20, shadowColor: theme.colors.secondary },
      }),
    },
    navTopCol: {
      flex: 1,
      flexGrow: 1,
      flexDirection: "row",
    },
    domusoIconButtonStyle: {
      paddingRight: 70,
      paddingVertical: 10,
      ...domusoResponsiveStyleHelper({
        webPhone: { paddingLeft: 10 },
        webTablet: { paddingLeft: 10 },
        webDesktop: { paddingLeft: 10 },
        iosPhone: { paddingLeft: 10 },
        iosTablet: { paddingLeft: 10 },
        androidPhone: { paddingLeft: 10 },
        androidTablet: { paddingLeft: 10 },
      }),
    },
    menuProfileIconContainer: {},
    triggerTouchableStyle: {
      outlineStyle: "none",
      paddingBottom: 5,
      ...domusoResponsiveStyleHelper({
        webPhone: { paddingHorizontal: 20, paddingTop: 10 },
        webTablet: { paddingHorizontal: 30, paddingTop: 10 },
        webDesktop: { paddingHorizontal: 40, paddingTop: 15 },
        iosPhone: { paddingHorizontal: 20, paddingTop: 10 },
        iosTablet: { paddingHorizontal: 30, paddingTop: 10 },
        androidPhone: { paddingHorizontal: 20, paddingTop: 10 },
        androidTablet: { paddingHorizontal: 30, paddingTop: 10 },
      }),
    },
    nav: {
      display: "flex",
      backgroundColor: theme.colors.filter,
      justifyContent: "center",
      width: "100%",
      height: 50,
    },
    navInner: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      width: "100%",
    },
    back: {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "flex-start",
      color: theme.colors.secondary,
      paddingLeft: "5%",
    },
    title: {
      color: theme.colors.secondary,
      fontWeight: "bold",
      alignSelf: "center",
      textAlign: "center",
      fontFamily: "Lato_700Bold",
    },
    content: {},
    property: {
      paddingTop: 30,
      marginTop: 20,
      marginBottom: 60,
    },
    paymentSteps: {
      width: "100%",
    },

    cardContainer: {
      width: "92%",
      display: "flex",
      flex: 1,
      height: 80,
      position: "absolute",
      flexDirection: "row",
      // Half of 8
      left: "4%",
      zIndex: 2,
      elevation: 3,
      backgroundColor: theme.colors.white,
      shadowColor: theme.colors.darkBg,
      shadowOffset: {
        width: 0,
        height: 2,
      },
      shadowOpacity: 0.24,
      shadowRadius: 3,
    },
    headline: {
      textAlign: "center",
      fontSize: theme.fontSize.title2,
      fontWeight: "700",
    },
    textContainer: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
    },
  });
};
